import { InputGroup } from "@smartsuite/react-ui/lib";
import { SliderProgressInputField } from "@smartsuite/react-ui/lib/components/fields/Number/SliderProgress/SliderProgressInputField/SliderProgressInputField";
import type { AppStructureFieldParams, NumberSliderFieldValue } from "@smartsuite/types";
import React, { useMemo } from "react";
import { FormFieldControl } from "../../../../components/FormFieldControl/FormFieldControl";
import { useFormField } from "../../../../hooks/useFormField";
import type { FormFieldProps } from "../../../types";
import { validateByType } from "../../text/validation";

import { getSliderSize } from "./NumberSliderFieldControl.helpers";
import "./NumberSliderFieldControl.sass";

export type NumberSliderFieldControlProps = FormFieldProps;

export const NumberSliderFieldControl = ({
  caption,
  context,
  field,
  formItem,
  label,
  name,
  required,
  readOnly,
}: NumberSliderFieldControlProps): JSX.Element => {
  const formField = useFormField<NumberSliderFieldValue>({
    name,
    validate: validateByType("number", field, formItem),
  });

  const fieldParameters = useMemo(
    (): AppStructureFieldParams => ({
      min_value: field.params.min_value,
      max_value: field.params.max_value,
      value_increment: field.params.value_increment,
    }),
    [field.params.min_value, field.params.max_value, field.params.value_increment],
  );

  const handleOnChangeFocused = (isFocused: boolean): void => {
    if (isFocused) {
      formField.onFocus();
      return;
    }

    formField.onBlur();
  };

  const handleOnChangeValue = (value: number | null): void => {
    formField.onFocus();
    formField.onChange(value);
  };

  const size = getSliderSize(Number(field.params.display_format ?? 1));

  return (
    <FormFieldControl
      captionAsTooltip={formItem.params.help_text_as_tooltip}
      caption={caption}
      helpText={formItem.params.helpText}
      errorMessage={formField.errorMessage}
      label={label}
      required={required}
      state={formField.state}
      readOnly={readOnly}
    >
      <InputGroup
        className="form-number-field-group"
        inputBackground="white-new"
        state={formField.state}
      >
        <SliderProgressInputField
          value={formField.value}
          fieldControlState={formField.state}
          isFocused={formField.state === "active"}
          isReadOnly={field.params.read_only}
          isShowHandle={!field.params.read_only && formField.state !== "default"}
          size={size}
          userPreferences={context.preferences}
          validateOn="blur"
          parameters={fieldParameters}
          color={field.params.color ?? ""}
          className="number-slider-progress-edit-record"
          onChangeFocused={handleOnChangeFocused}
          onChangeValue={handleOnChangeValue}
        />
      </InputGroup>
    </FormFieldControl>
  );
};
